import { Effect, type Scope } from 'effect';
import { attemptStudioPromise, type StudioRuntimeError } from '../effect-runtime';
import type { StudioContextValue } from './workspace';

/** The workspace transfers canvas ownership; late canvases are released instead of adopted. */
export const withStudioPageCanvasEffect = Effect.fn('studio.pageCanvas')(function* <A>(
  renderPage: StudioContextValue['renderPage'],
  page: number,
  scale: number,
  use: (canvas: HTMLCanvasElement) => Effect.Effect<A, StudioRuntimeError>,
): Effect.fn.Return<A, StudioRuntimeError, Scope.Scope> {
  const owner = yield* Effect.acquireRelease(
    Effect.sync(() => ({
      controller: new AbortController(),
      canvas: undefined as HTMLCanvasElement | undefined,
    })),
    (owner) =>
      Effect.sync(() => {
        owner.controller.abort();
        if (owner.canvas) {
          owner.canvas.width = 0;
          owner.canvas.height = 0;
        }
      }),
  );
  const canvas = yield* attemptStudioPromise('pdfjs', 'page-render', async () => {
    const canvas = await renderPage(page, scale, { signal: owner.controller.signal });
    if (owner.controller.signal.aborted) {
      canvas.width = 0;
      canvas.height = 0;
      throw new Error('Page rendering cancelled');
    }
    owner.canvas = canvas;
    return canvas;
  });
  return yield* use(canvas);
}, Effect.scoped);
